import type {
  ExportPermissions,
  UserDataResponse,
} from "../Interfaces/LoginInterface";
import type { OverviewExportDeliveryMode } from "../Apis/overviewExportTypes";

export type ExportEntity = "patientsContacts" | "opportunities" | "tasks";

export type ExportDeliveryChannelMode =
  "both" | "email-only" | "in-app-only" | "none";

export type ModulePermissionsMap = NonNullable<
  UserDataResponse["permissionGroup"]["modulePermissions"]
>;

export function isTenantOrFacilityAdmin(roles: string[] | undefined): boolean {
  return (
    Array.isArray(roles) &&
    roles.some(
      (r) =>
        r === "SUPER_ADMIN" || r === "TENANT_ADMIN" || r === "FACILITY_ADMIN"
    )
  );
}

export function hasModulePermissionsObject(
  modulePermissions: ModulePermissionsMap | null | undefined
): boolean {
  return (
    modulePermissions != null &&
    typeof modulePermissions === "object" &&
    Object.keys(modulePermissions).length > 0
  );
}

export interface PermissionAccessContext {
  modulePermissions: ModulePermissionsMap | undefined;
  isTenantOrFacilityAdmin: boolean;
  hasPermissionGroup: boolean;
  hasModulePermissions: boolean;
  resolveModuleAccess: (hasSpecificPermission: boolean) => boolean;
}

/**
 * TENANT_ADMIN / FACILITY_ADMIN with no permission group (or empty modules) are unrestricted.
 * Other users need an assigned group with the specific module permission.
 */
export function getPermissionAccessContext(
  userProfile: UserDataResponse | null | undefined
): PermissionAccessContext {
  const modulePermissions = userProfile?.permissionGroup?.modulePermissions;
  const userRoles = userProfile?.user?.roles ?? [];
  const admin = isTenantOrFacilityAdmin(userRoles);
  const hasPermissionGroup = !!userProfile?.permissionGroup;
  const hasModulePermissions = hasModulePermissionsObject(modulePermissions);

  const resolveModuleAccess = (hasSpecificPermission: boolean): boolean => {
    if (admin) {
      if (!hasPermissionGroup) return true;
      if (!hasModulePermissions) return true;
      return hasSpecificPermission;
    }
    return hasPermissionGroup && hasModulePermissions && hasSpecificPermission;
  };

  return {
    modulePermissions,
    isTenantOrFacilityAdmin: admin,
    hasPermissionGroup,
    hasModulePermissions,
    resolveModuleAccess,
  };
}

function hasExportPermissionsObject(
  exportPermissions: ExportPermissions | null | undefined
): exportPermissions is ExportPermissions {
  return exportPermissions != null && typeof exportPermissions === "object";
}

/**
 * Whether the user may export the given entity from Overview.
 * SUPER_ADMIN / TENANT_ADMIN / FACILITY_ADMIN always allowed; others need exportPermissions.
 * Default when exportPermissions absent: false.
 */
export function canExportEntity(
  userProfile: UserDataResponse | null | undefined,
  entity: ExportEntity
): boolean {
  const roles = userProfile?.user?.roles ?? [];
  if (isTenantOrFacilityAdmin(roles)) return true;

  const exportPermissions = userProfile?.permissionGroup?.exportPermissions;
  if (!hasExportPermissionsObject(exportPermissions)) {
    return false;
  }
  if (!exportPermissions.enabled) return false;
  return exportPermissions[entity] === true;
}

function resolveExportPermissionsForUser(
  userProfile: UserDataResponse | null | undefined
): ExportPermissions | null {
  const roles = userProfile?.user?.roles ?? [];
  if (isTenantOrFacilityAdmin(roles)) {
    return {
      enabled: true,
      opportunities: true,
      patientsContacts: true,
      tasks: true,
      emailDelivery: true,
      inAppDownload: true,
      allowCustomEmailRecipient: true,
    };
  }
  const exportPermissions = userProfile?.permissionGroup?.exportPermissions;
  if (!hasExportPermissionsObject(exportPermissions)) return null;
  return exportPermissions;
}

/** CAR-1179: render in-app Download when the group grants inAppDownload. */
export function canDownloadInApp(
  userProfile: UserDataResponse | null | undefined
): boolean {
  const ep = resolveExportPermissionsForUser(userProfile);
  return ep?.inAppDownload === true;
}

/** CAR-1179: show custom-recipient input when granted. */
export function canPickCustomRecipient(
  userProfile: UserDataResponse | null | undefined
): boolean {
  const ep = resolveExportPermissionsForUser(userProfile);
  return ep?.allowCustomEmailRecipient === true;
}

/** CAR-1179: which delivery channels the user's group grants. */
export function getExportDeliveryChannelMode(
  userProfile: UserDataResponse | null | undefined
): ExportDeliveryChannelMode {
  const ep = resolveExportPermissionsForUser(userProfile);
  if (!ep?.enabled) return "none";
  const email = ep.emailDelivery === true;
  const inApp = ep.inAppDownload === true;
  if (email && inApp) return "both";
  if (email) return "email-only";
  if (inApp) return "in-app-only";
  return "none";
}

/** Whether the export wizard should show the delivery-choice step. */
export function shouldShowExportDeliveryChoice(
  userProfile: UserDataResponse | null | undefined
): boolean {
  return getExportDeliveryChannelMode(userProfile) === "both";
}

/** Whether the export wizard should skip the email recipient step. */
export function shouldSkipExportEmailStep(
  userProfile: UserDataResponse | null | undefined
): boolean {
  return getExportDeliveryChannelMode(userProfile) === "in-app-only";
}

/** Resolve deliveryMode to send on POST (omit when single-channel group). */
export function resolveExportDeliveryModeForRequest(
  userProfile: UserDataResponse | null | undefined,
  selectedMode?: OverviewExportDeliveryMode
): OverviewExportDeliveryMode | undefined {
  const channelMode = getExportDeliveryChannelMode(userProfile);
  if (channelMode === "both") return selectedMode ?? "EMAIL";
  if (channelMode === "email-only") return undefined;
  if (channelMode === "in-app-only") return undefined;
  return undefined;
}
